import React, { lazy, Suspense, useEffect } from 'react'

import {
  useGetLabels,
  useBreakpoint,
  getLocale,
  currentBrand,
  REACT_BASE_ROUTES,
  getDSTokensCssCustomProperties,
} from '@pb/utils'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Router, Switch } from 'react-router-dom'

import Title from 'App/Title'
import { triggerDispatchOfQueue } from 'helpers/localStorage'
import 'helpers/startsWithPolyfill'

import {
  CART_ROUTES,
  PAYMENTS_ROUTES,
  ACCOUNT_ROUTES,
  FAVOURITES_ROUTES,
  LINK_ACCOUNT_ROUTES,
  STORE_LOCATOR_ROUTES,
  PUBLIC_PROFILE_ROUTES,
  REGISTER_ROUTES,
  LOGIN_ROUTES,
  CHECKOUT_ROUTES,
  DIGITAL_STYLIST_LANDING_ROUTES,
  STYLEBOARD_ROUTES,
  STYLESTORY_ROUTES,
  RETURNS_ROUTES,
  RATEANDREVIEWS_ROUTES,
  STYLETEST_ROUTES,
} from './constants'
import DevToolbar from './DevToolbar'
import {
  operationFetchConfig,
  operationFetchSettings,
  operationFetchUser,
  operationSetScreenType,
} from './duck/operations'
import selectors from './duck/selectors'
import ErrorBoundary from './ErrorBoundary'
import { teliumTrackingScript } from './helpers/tealiumTrackingScript'
import history from './history'
import ScrollToTop from './ScrollToTop'

const Account = lazy(() => import(/* webpackChunkName: "account" */ 'features/account'))
const DigitalStylistEditorial = lazy(
  () =>
    import(
      /* webpackChunkName: "account" */ 'features/account/digital-stylist/components/QuizEditorialPage'
    ),
)

const Cart = lazy(() => import(/* webpackChunkName: "cart" */ 'features/cart'))
const Checkout = lazy(() => import(/* webpackChunkName: "checkout" */ 'features/checkout'))
const Favourites = lazy(() => import(/* webpackChunkName: "favourites" */ 'features/favourites'))
const PublicProfile = lazy(
  () => import(/* webpackChunkName: "publicProfile" */ 'features/publicProfile'),
)
const LinkAccounts = lazy(
  () => import(/* webpackChunkName: "linkAccounts" */ 'features/linkAccounts'),
)
const StoreLocator = lazy(
  () => import(/* webpackChunkName: "storeLocator" */ 'features/storeLocator'),
)
const Header = lazy(() => import(/* webpackChunkName: "header" */ 'features/header'))
const Footer = lazy(() => import(/* webpackChunkName: "footer" */ 'features/footer'))
const Register = lazy(() => import(/* webpackChunkName: "register" */ 'features/register'))
const Login = lazy(() => import(/* webpackChunkName: "login" */ 'features/login'))
const Styleboard = lazy(() => import(/* webpackChunkName: "login" */ 'features/Styleboard'))
const StyleStories = lazy(
  () => import(/* webpackChunkName: "stylestories" */ 'features/styleStory'),
)
const Returns = lazy(() => import(/* webpackChunkName: "Returns" */ 'features/returns'))

const UnreviewedProducts = lazy(
  () => import(/* webpackChunkName: "unreviewedproducts" */ 'features/UnreviewedProducts'),
)

const StyleTest = lazy(() => import(/* webpackChunkName: "styletest" */ 'features/StyleTest'))

const App: React.FC = () => {
  const dispatch = useDispatch()
  const isAccount = window.location.pathname?.includes(
    `${getLocale()}${REACT_BASE_ROUTES.Account.path}`,
  )

  const setScreenType = (screenSize): any => dispatch(operationSetScreenType(screenSize))
  const triggerDispatchQueue = (): any => triggerDispatchOfQueue(dispatch)
  const fetchUser = (): any => dispatch(operationFetchUser())
  const fetchSettings = (): any => dispatch(operationFetchSettings())
  const fetchConfig = (): any => dispatch(operationFetchConfig(getLocale()))

  const shouldRenderUsers = process.env.IS_USER_API_ENABLED === 'enabled'
  const shouldRenderSettings = process.env.IS_SETTINGS_API_ENABLED === 'enabled'
  const shouldRenderConfig = process.env.IS_CONFIG_API_ENABLED === 'enabled'
  const shouldRenderReactFooter =
    process.env.IS_REACT_FOOTER_ENABLED === 'enabled' && history.location.pathname !== '/checkout-r'
  const shouldRenderReRoutedCart = process.env.IS_REACT_CART_RE_ROUTING_ENABLED === 'enabled'
  const shouldRenderReactHeader =
    process.env.IS_REACT_HEADER_ENABLED === 'enabled' && history.location.pathname !== '/checkout-r'

  const shouldFetchSettings =
    useSelector((state) => selectors.getShouldFetchSettings(state)) && shouldRenderSettings
  const shouldFetchUser =
    useSelector((state) => selectors.getShouldFetchUser(state)) && shouldRenderUsers
  const isInitialLoadCompleted = useSelector((state) => selectors.getIsInitialLoadCompleted(state))

  const { isFetchingLabels } = useGetLabels()

  const screenSize = useBreakpoint()

  useEffect(() => {
    setScreenType(screenSize)
  }, [screenSize])

  useEffect(() => {
    if (shouldFetchSettings) {
      fetchSettings()
    }

    // TODO: User API should only be fetched if user is logged in or in Checkout (not on cart for guest etc.)
    if (shouldFetchUser || isAccount) {
      fetchUser()
    }

    if (shouldRenderConfig) {
      fetchConfig()
    }

    triggerDispatchQueue()

    if (currentBrand !== 'hm') {
      teliumTrackingScript()
    }
  }, [])

  if (isInitialLoadCompleted && !isFetchingLabels) {
    return (
      <>
        <link rel="preload stylesheet" href={getDSTokensCssCustomProperties()} as="style" />
        <Router history={history}>
          <Title />
          {shouldRenderReactHeader && (
            <Suspense fallback={<div />}>
              <Header />
            </Suspense>
          )}

          <ErrorBoundary>
            <Suspense fallback={<div style={{ height: '100vh' }} />}>
              <ScrollToTop>
                <Switch>
                  {process.env.IS_ACCOUNT_RATE_AND_REVIEW_PAGE_ENABLED === 'enabled' && (
                    <Route
                      exact
                      key={RATEANDREVIEWS_ROUTES.UnreviewedProductsPage.path}
                      path={RATEANDREVIEWS_ROUTES.UnreviewedProductsPage.path}
                      component={UnreviewedProducts}
                    />
                  )}
                  {process.env.IS_REACT_ACCOUNT_ENABLED === 'enabled' && (
                    <Route path={ACCOUNT_ROUTES.AccountOverview.path} component={Account} />
                  )}

                  {process.env.IS_REACT_CART_ENABLED === 'enabled' ? (
                    <Route
                      path={[
                        shouldRenderReRoutedCart
                          ? CART_ROUTES.CartOverview.reroutePath
                          : CART_ROUTES.CartOverview.path,
                        PAYMENTS_ROUTES.PaymentMethods.path,
                      ]}
                      component={Cart}
                    />
                  ) : (
                    process.env.IS_REACT_CART_RE_ROUTING_PRODUCTION_FALLBACK_ENABLED ===
                      'enabled' && (
                      <Route
                        path={CART_ROUTES.CartOverview.reroutePath}
                        component={() => {
                          const currentUrl = window.location.href
                          const getMarketSpecificUrl = currentUrl.match(/.*?(?=cart-r|$)/i)?.[0]
                          if (getMarketSpecificUrl) {
                            const redirectUrl = `${getMarketSpecificUrl}cart`
                            window.location.href = redirectUrl
                          }
                          return null
                        }}
                      />
                    )
                  )}
                  <Route
                    exact
                    path={CHECKOUT_ROUTES.CheckoutOverview.reroutePath}
                    component={Checkout}
                  />
                  <Route exact path={CHECKOUT_ROUTES.CheckoutOverview.path} component={Checkout} />
                  {process.env.IS_DIGITAL_RETURNS_ENABLED === 'enabled' && (
                    <Route path={RETURNS_ROUTES.ReturnsOverview.path} component={Returns} />
                  )}
                  {process.env.IS_REACT_FAVOURITES_ENABLED === 'enabled' && (
                    <Route
                      path={FAVOURITES_ROUTES.FavouritesOverview.path}
                      component={Favourites}
                    />
                  )}
                  {process.env.IS_LINK_ACCOUNT_URL_ENABLED === 'enabled' && (
                    <Route
                      path={LINK_ACCOUNT_ROUTES.LinkAccountsOverview.path}
                      component={LinkAccounts}
                    />
                  )}
                  {process.env.IS_STYLEBOARD_ENABLED === 'enabled' && (
                    <Route path={STYLEBOARD_ROUTES.StyleboardLanding.path} component={Styleboard} />
                  )}
                  {process.env.IS_PUBLIC_PROFILE_ENABLED === 'enabled' &&
                    process.env.IS_STYLEBOARD_ENABLED === 'enabled' && (
                      <Route
                        path={PUBLIC_PROFILE_ROUTES.PublicProfileOverview.path}
                        component={PublicProfile}
                      />
                    )}
                  {process.env.IS_STYLESTORY_ENABLED === 'enabled' && (
                    <Route path={STYLESTORY_ROUTES.StyleStories.path} component={StyleStories} />
                  )}
                  {process.env.IS_STYLETEST_ENABLED === 'enabled' && (
                    <Route path={STYLETEST_ROUTES.StyleTest.path} component={StyleTest} />
                  )}
                  {process.env.IS_REACT_STORELOCATOR_ENABLED === 'enabled' && (
                    <Route
                      path={STORE_LOCATOR_ROUTES.StoreLocatorOverview.path}
                      component={StoreLocator}
                    />
                  )}
                  {process.env.IS_REACT_DIGITAL_STYLIST_EDITORIAL_ENABLED === 'enabled' && (
                    <Route
                      path={DIGITAL_STYLIST_LANDING_ROUTES.DigitalStylistOverview.path}
                      component={DigitalStylistEditorial}
                    />
                  )}
                  {process.env.IS_REACT_REGISTER_ENABLED === 'enabled' && (
                    <Route path={REGISTER_ROUTES.RegisterOverview.path} component={Register} />
                  )}
                  {(process.env.IS_REACT_LOGIN_ENABLED === 'enabled' ||
                    process.env.IS_REACT_LOGIN_CHECKOUT_ENABLED === 'enabled') && (
                    <Route path={LOGIN_ROUTES.LoginOverview.path} component={Login} />
                  )}
                </Switch>
              </ScrollToTop>
            </Suspense>
          </ErrorBoundary>

          {shouldRenderReactFooter && (
            <Suspense fallback={<div />}>
              <Footer />
            </Suspense>
          )}
        </Router>
        <DevToolbar />
      </>
    )
  }
  return null
}

export default App
